import React, {useCallback} from 'react'
import {useHubspotForm} from 'src/Event/hooks/useHubspotForm'
import {REPORT_COPYRIGHT_NOTICE} from '../data/legal'
import {useSpendBenchmarksTracking} from '../hooks/useSpendBenchmarksTracking'

const CHECKLIST = [
  {
    title: 'Detailed vendor profiles across 19 categories',
    desc: 'Adoption, real spend by company size, growth trajectory.',
  },
  {title: 'Switching & migration data', desc: "Who's winning, who's losing, where companies are moving."},
  {
    title: 'Size-specific benchmarks (1–19, 20–99, 100+)',
    desc: 'What companies your size actually spend per category.',
  },
  {title: 'European-only dataset', desc: 'Built from EU company transactions.'},
  {title: 'Head-to-head comparisons', desc: 'Direct matchups for the top vendors in every category.'},
  {title: 'New-adopter analysis', desc: 'What first-time buyers are choosing right now.'},
]

interface DownloadCtaProps {
  pdfUrl: string
  hubspotFormId: string | null | undefined
}

const DownloadCta: React.FC<DownloadCtaProps> = ({pdfUrl, hubspotFormId}) => {
  const {trackClick} = useSpendBenchmarksTracking()

  const triggerDownload = useCallback(async () => {
    window.open(pdfUrl, '_blank', 'noopener,noreferrer')

    try {
      const blob = await fetch(pdfUrl).then((r) => r.blob())
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = 'European_SaaS_Spending_Report_2026.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(objectUrl)
    } catch {
      // window.open above already opened the file — silently ignore blob download failure
    }
  }, [pdfUrl])

  const {form, submit, isSubmitSuccessful, isSubmitting} = useHubspotForm({
    formId: hubspotFormId,
    pageUid: 'saas-spend-report-2026',
    trackingLocation: 'spend-benchmarks',
    trackDataType: 'pdf-download',
    onSuccess: triggerDownload,
  })

  const {register, handleSubmit} = form

  return (
    <section id="download" className="final-cta">
      <div className="wrap">
        <div className="eyebrow eyebrow-dot" style={{color: 'var(--moss-neon)'}}>
          The full report
        </div>
        <h2>
          Get the <span className="neon">full European SaaS &amp; AI</span> intelligence report.
        </h2>
        <p className="hero-sub" style={{maxWidth: 780, marginTop: -8}}>
          Everything on this page, plus the data we couldn't fit on a single screen. Free. Just tell us your work email.
        </p>
        <div className="grid">
          <div>
            <ul className="includes-list">
              {CHECKLIST.map((item) => (
                <li key={item.title}>
                  <span className="check">&#10003;</span>
                  <div>
                    <div className="title">{item.title}</div>
                    <div className="desc">{item.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="proof-strip">
              Based on <em>€302M in real transactions</em> across 20,088 vendors, Jan 2024 – May 2026. Not surveys. Not
              estimates. Actual spend.
            </div>
          </div>
          {isSubmitSuccessful ? (
            <div
              className="cta-form-card"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 16,
              }}
            >
              <div style={{fontSize: 48}}>&#10003;</div>
              <h3 style={{margin: 0}}>Your report is downloading.</h3>
              <p style={{color: 'rgba(255,255,255,0.6)', margin: 0}}>
                Didn't start?{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    triggerDownload()
                  }}
                  style={{color: 'var(--moss-neon)'}}
                >
                  Click here to download.
                </a>
              </p>
              <p className="download-copyright-notice">{REPORT_COPYRIGHT_NOTICE}</p>
            </div>
          ) : (
            <form className="cta-form-card" onSubmit={handleSubmit(submit)}>
              <div className="formlabel">Work email</div>
              <input
                {...register('email')}
                className="field"
                type="email"
                placeholder="finance@yourcompany.com"
                required
              />
              <div className="formlabel">Company size</div>
              <select {...register('company_size')} className="field" required>
                <option value="">Select size…</option>
                <option>1–9 employees</option>
                <option>10–19 employees</option>
                <option>20–49 employees</option>
                <option>50–99 employees</option>
                <option>100–249 employees</option>
                <option>250+ employees</option>
              </select>
              <div className="formlabel">
                What's most interesting to you?{' '}
                <span style={{textTransform: 'none', letterSpacing: 0, color: 'rgba(255,255,255,0.4)'}}>
                  (optional)
                </span>
              </div>
              <select {...register('interest')} className="field">
                <option value="">Choose one…</option>
                <option>AI adoption</option>
                <option>Vendor switching</option>
                <option>Spend benchmarks</option>
                <option>Category breakdowns</option>
              </select>
              <button
                className="btn btn-primary btn-lg submit"
                type="submit"
                disabled={isSubmitting}
                onClick={() => trackClick('cta_download_report_button')}
              >
                {isSubmitting ? 'SUBMITTING…' : 'DOWNLOAD FULL REPORT'}
              </button>
              <div className="text-body-xs mt-4 text-center text-moss-white">
                By downloading this report, you acknowledge our{' '}
                <a
                  href="https://www.getmoss.com/legal"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-accent-neon"
                >
                  privacy policy
                </a>
                ,{' '}
                <a
                  href="https://getmoss.com/public/terms-and-conditions/20240701_INT-en_User_Marketing_Consent_v2.0.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-accent-neon"
                >
                  consent
                </a>{' '}
                to receive Moss updates (which you may opt out of at any time), and agree to the copyright and usage
                restrictions set out below.
              </div>
              <p className="download-copyright-notice">{REPORT_COPYRIGHT_NOTICE}</p>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}

export default DownloadCta
